import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ConnectedOverlayPositionChange, ConnectedPosition, ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { getQuotationStatusStyles } from 'src/app/shared/utils/quotation-status-styles';
import { IDuplicateInfo } from '../../models/quotations.models';
import { OrderStatusConfirmComponent } from '../order-status-confirm/order-status-confirm.component';
import { DialogAction, DialogProps } from '../order-status-confirm/order-status-confirm.types';

/** Gap between the badge and the panel, in px. */
const PANEL_GAP = 8;

/** Grace period so the pointer can cross the gap from badge to panel. */
const CLOSE_DELAY_MS = 150;

@Component({
  selector: 'app-duplicate-badge',
  templateUrl: './duplicate-badge.component.html',
  styleUrls: ['./duplicate-badge.component.scss'],
})
export class DuplicateBadgeComponent implements OnDestroy {
  @Input() duplicateInfo!: IDuplicateInfo;

  /**
   * `big` matches the header tag strip (`app-order-header-tags`, WB-7938);
   * `small` is the orders-table row pill, which stays denser on purpose.
   */
  @Input() size: 'small' | 'big' = 'small';

  @Output() dismissed = new EventEmitter<void>();

  panelPinned = false;

  /** True while the panel is rendered above/below and/or right-aligned. */
  panelAbove = false;
  panelAlignEnd = false;

  /**
   * Fallback placements, tried in order until one fits the viewport. The panel
   * is 400px wide, so a badge near the right edge needs the end-aligned
   * variants, and a badge near the bottom of a long table needs the above ones.
   */
  readonly panelPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: PANEL_GAP },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: PANEL_GAP },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -PANEL_GAP },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -PANEL_GAP },
  ];

  /** Keeps the panel glued to the badge while the orders table scrolls. */
  readonly scrollStrategy: ScrollStrategy;

  private hoverOpen = false;
  private closeTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private elRef: ElementRef,
    private dialog: MatDialog,
    scrollStrategyOptions: ScrollStrategyOptions,
  ) {
    this.scrollStrategy = scrollStrategyOptions.reposition({ autoClose: true });
  }

  ngOnDestroy(): void {
    this.clearCloseTimer();
  }

  // ── Open / close ───────────────────────────────────────────────────────────

  get isPanelOpen(): boolean {
    return this.hoverOpen || this.panelPinned;
  }

  /** Pointer entered the badge or the panel. */
  onPointerEnter(): void {
    this.clearCloseTimer();
    this.hoverOpen = true;
  }

  /**
   * Pointer left the badge or the panel. Closing is deferred so the pointer can
   * cross the gap between the two without the panel vanishing — the panel is in
   * a body-level overlay, so a CSS hover bridge is no longer possible.
   */
  onPointerLeave(): void {
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => (this.hoverOpen = false), CLOSE_DELAY_MS);
  }

  togglePin(event: MouseEvent): void {
    event.stopPropagation();
    this.panelPinned = !this.panelPinned;
  }

  /** Click landed outside the overlay; ignore clicks on the badge itself. */
  onOutsideClick(event: MouseEvent): void {
    if (this.elRef.nativeElement.contains(event.target)) return;
    this.panelPinned = false;
    this.hoverOpen = false;
  }

  /** Mirror the placement the overlay actually resolved to, so the arrow follows. */
  onPositionChange(change: ConnectedOverlayPositionChange): void {
    this.panelAbove = change.connectionPair.overlayY === 'bottom';
    this.panelAlignEnd = change.connectionPair.overlayX === 'end';
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = undefined;
  }

  navigateTo(id: number, event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate(['/quotations', id], { queryParamsHandling: 'merge' });
  }

  // Confirmed rather than immediate: the flag is permanent and shared by every
  // user, and the undo lives in the row menu rather than here, so a misclick is
  // not obviously recoverable from where it happens.
  dismiss(event: MouseEvent): void {
    event.stopPropagation();
    this.panelPinned = false;

    const actions: DialogAction[] = [
      {
        label: this.translateService.instant('quotations_and_orders.status_dialog.actions.go_back'),
        class: 'action-light',
        callback: () => this.dialog.closeAll(),
      },
      {
        label: this.translateService.instant('quotations_and_orders.duplicate_dismiss_action'),
        class: 'action-primary',
        icon: { position: 'after', name: 'arrow_forward' },
        callback: () => {
          this.dismissed.emit();
          this.dialog.closeAll();
        },
      },
    ];

    const data: DialogProps = {
      title: this.translateService.instant('quotations_and_orders.duplicate_dismiss_confirm_title'),
      description: this.translateService.instant('quotations_and_orders.duplicate_dismiss_confirm_body'),
      mainIcon: 'assets/icones/warning.svg',
      actions,
    };
    this.dialog.open(OrderStatusConfirmComponent, { data });
  }

  // Reads the one status→colour map the orders table and both order headers
  // share, rather than the switch this component used to carry (WB-7938).
  getStatusStyles(status: string): { [key: string]: string } {
    return getQuotationStatusStyles(status);
  }

  getStatusTranslation(status: string): string {
    return this.translateService.instant(`quotations_and_orders.QuotationStatus.${status}`) || status;
  }
}
